import type { LucideIcon } from 'lucide-react';
import { ClipboardCheck, LifeBuoy, ListChecks, Settings, Users } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Async } from '@/shared/ui/Async';
import { Button } from '@/shared/ui/Button';

import { useDashboardStats } from '../hooks/use-admin';

interface StatCardProps {
  labelKey: string;
  value: number;
  Icon: LucideIcon;
  onClick: () => void;
}

function StatCard({ labelKey, value, Icon, onClick }: StatCardProps): JSX.Element {
  const { t } = useTranslation();
  return (
    <button type="button" className="stat-card" onClick={onClick}>
      <div className="stat-card__icon" aria-hidden>
        <Icon size={20} />
      </div>
      <div className="stat-card__value">{value}</div>
      <div className="stat-card__label">{t(labelKey)}</div>
    </button>
  );
}

/**
 * Admin dashboard landing (per design handoff §Admin journey): 4 stat cards
 * for the ops-critical counts, each a shortcut into its detail surface.
 * Replaces the old AdminRoute which crammed providers + credentials into one
 * page.
 */
export function AdminDashboardRoute(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const stats = useDashboardStats();

  return (
    <main className="page stack">
      <h1>{t('admin.dashboardTitle')}</h1>
      <Async query={stats}>
        {(s) => (
          <div className="stat-grid">
            <StatCard
              labelKey="admin.statOpenRequests"
              value={s.openRequests}
              Icon={ListChecks}
              onClick={() => navigate('/admin/map')}
            />
            <StatCard
              labelKey="admin.statActiveFreelancers"
              value={s.activeFreelancers}
              Icon={Users}
              onClick={() => navigate('/admin/users')}
            />
            <StatCard
              labelKey="admin.statPendingVerifications"
              value={s.pendingVerifications}
              Icon={ClipboardCheck}
              onClick={() => navigate('/admin/verifications')}
            />
            <StatCard
              labelKey="admin.statOpenTickets"
              value={s.openTickets}
              Icon={LifeBuoy}
              onClick={() => navigate('/admin/tickets')}
            />
          </div>
        )}
      </Async>
      <section className="card row row--between">
        <div className="row">
          <Settings size={20} aria-hidden />
          <span>{t('admin.settingsLink')}</span>
        </div>
        <Button variant="outline" onClick={() => navigate('/admin/settings')}>
          {t('admin.settingsCardCta')}
        </Button>
      </section>
    </main>
  );
}
